<script lang="ts">
    import { intersectionObserver } from '@amp/web-app-components/src/actions/intersection-observer';
    import PlaybackItem from '~/components/structure/Players/components/MobileNowPlaying/PlaybackItem.svelte';
    import NowPlayingQueueList from './NowPlayingQueueList.svelte';
    import { getI18n } from '~/stores/i18n';
    import type { QueueItemMetadata } from '~/jet/models';
    import type { ItemMap } from './types';
    import { createEventDispatcher } from 'svelte';

    const panelId = 'up-next';
    const dispatch = createEventDispatcher();
    const i18n = getI18n();

    // Now Playing Item to show at top of queue in Mobile view
    export let item: QueueItemMetadata | null;

    // Element to tell context menu where to render menu options
    // Only used by PlaybackItem in mobile view
    export let parentElement: HTMLElement = null;

    // Flag to modify how the queue is displayed
    export let isMobileView: boolean = false;
    // export let contextualMenuConfigWithParent: ContextualMenuConfigWithParent;
    export let clearButtonLabel: string;
    export let emptyQueueText: string;
    export let isEmpty: boolean = true;
    export let queueHeading: string;
    export let showClearButton: boolean = false;
    export let unplayedItems: ItemMap[];

    // Helper variables used with intersection observer for sticky header styles
    let nowPlayingItemIsVisible: boolean = true;
    let playingNextQueueIsVisible: boolean = true;

    // Stick the Playing Next header when...
    // - Now Playing is *not* visible (scrolls off screen)
    // - Playing Next queue *is* visible (list items are still on screen)
    // Otherwise, Playing Next header should scroll with screen contents.
    $: isHeaderStuck = !nowPlayingItemIsVisible && playingNextQueueIsVisible;

    const handleNowPlayingItemVisibility = (isIntersecting: boolean) => {
        nowPlayingItemIsVisible = isIntersecting;
    };

    function handleKeyboardEvent(e: KeyboardEvent) {
        // Preventing default here ensures the sidebar doesn't
        // scroll as the user navigates up/down with arrow keys
        if (!isMobileView) {
            e.preventDefault();
            dispatch('keyboardEvent', e);
        }
    }
</script>

<div
    data-testid="now-playing-queue"
    class="now-playing-queue"
    class:now-playing-queue--desktop={!isMobileView}
    class:now-playing-cropped={isHeaderStuck}
>
    <!-- Now Playing item controls -->
    {#if item && isMobileView}
        <div
            class="now-playing"
            data-testid="now-playing-playback-item"
            use:intersectionObserver={{
                callback: handleNowPlayingItemVisibility,
                threshold: 0,
            }}
            style:opacity={nowPlayingItemIsVisible ? 1 : 0}
        >
            <PlaybackItem
                layout="compact"
                {item}
                on:artworkClick
                bind:parentElement
                artworkButtonAriaLabel={$i18n.t(
                    'ASE.Web.MusicClassical.Controls.Expand',
                )}
            />
        </div>
    {/if}

    <div class="queue-section queue-section--playing-next">
        <div
            class="queue-header queue-header--sticky"
            class:is-sticking={isHeaderStuck}
            class:is-after-queue-list={!playingNextQueueIsVisible}
            data-testid="playing-next-header"
        >
            <div>
                <h4 class="queue-header__title">
                    {queueHeading}
                </h4>
            </div>

            {#if showClearButton}
                <button
                    class="playing-next-controls-clear"
                    data-testid="playing-next-clear"
                    on:click={() => dispatch('clearQueue')}
                >
                    {clearButtonLabel}
                </button>
            {/if}
        </div>

        {#if !isEmpty}
            <!-- Playing Next queue -->
            <div class="queue-list-container">
                <NowPlayingQueueList
                    {unplayedItems}
                    {panelId}
                    on:play
                    on:close
                    on:keydown={handleKeyboardEvent}
                />
            </div>
        {:else}
            <!-- Empty queue message -->
            <div class="queue-empty" data-testid="queue-empty">
                <p class="queue-empty-message">
                    {emptyQueueText}
                </p>
            </div>
        {/if}
    </div>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;

    $now-playing-item-height: 66px;
    $section-header-height: 72px;
    $bottom-fade-height: 70px;
    $track-column-gap: 12px;
    $track-height: 55px;
    $track-artwork-size: 48px;
    $track-time-size: 40px;

    // TODO: Improve this through slots?
    // rdar://110174109 ([Mobile Queue Parity] Improve mobile queue through slots)
    .now-playing-queue {
        height: 100%;

        &.now-playing-cropped {
            margin-top: $section-header-height; // Height of header
            padding-bottom: $bottom-fade-height + $section-header-height;
        }

        :global(.up-next-item) {
            // Remove default item padding -- spacing is handled by container margin
            padding-inline-start: 0;
            padding-inline-end: 0;
            grid-template-columns:
                [left-edge] $track-artwork-size [artwork-edge] calc(
                    100% - #{(2 * $track-column-gap) + $track-artwork-size + $track-time-size}
                )
                [title-edge] $track-time-size [right-edge];

            // Remove time and controls column in mobile queue to allow more space for title
            @media (--range-xsmall-down) {
                grid-template-columns: [left-edge] $track-artwork-size [artwork-edge] 1fr [title-edge] 0 [right-edge];
            }
        }

        :global(.up-next-item .metadata) {
            color: var(--systemSecondary-onDark);
            font: var(--subhead-emphasized);
        }

        // Temporary styles to disable "long press" selected state on mobile
        // rdar://109684345 ([Mobile Queue Parity]: Disable the Long Press Styles which Show Remove & Contextual Menu Icons)
        // TODO: rdar://150774509 ([Classical on Web] Queue selection state in Desktop Now Playing View)
        :global(.up-next-item.selected) {
            &:focus {
                background-color: transparent;
            }
        }

        // Keep time visible when contextual menu isn't displayed
        :global(.time-and-controls) {
            --timeOpacity: 1 !important; // stylelint-disable-line declaration-no-important
        }

        // Remove time, contextual menu, and item remove button
        :global(.time-and-controls),
        :global(.up-next-item-remove),
        :global(.controls) {
            @media (--range-xsmall-down) {
                display: none;
            }
        }

        :global(.controls) {
            --contextMenuEllipsisFillOverride: var(--systemPrimary-onDark);
        }

        // Prevent 'remove' button from being clipped due to overflow hidden on parent
        // 3.5px matches music app's button positioning
        :global(.up-next-item-remove) {
            $queueButtonOffset: 3.5px;
            --removeButtonOffset: -#{$queueButtonOffset};
            position: relative;
            top: calc($queueButtonOffset * 2);
        }
    }

    // in desktop view, set overflow on the inner queue list container
    // :where psuedo class prevents selector specificity from overriding
    // .now-playing-cropped styles in mobile view
    :where(.now-playing-queue:not(.now-playing-queue--desktop)),
    .now-playing-queue--desktop .queue-list-container {
        padding-bottom: $bottom-fade-height; // add extra scroll room for last item in list to scroll above fade effect
        overflow-y: auto;

        &::-webkit-scrollbar {
            display: none;
        }
    }

    .now-playing-queue--desktop {
        .queue-section {
            height: inherit;
        }

        .queue-list-container {
            height: calc(
                100% - 72px
            ); // queue list height minus queue list header height
        }
    }

    .now-playing,
    .queue-section {
        padding: 0 32px;
    }

    .queue-section {
        // Use Flexbox to allow us to render the container elements in a different order.
        // This helps smoothly scroll the sticky header out of view.
        display: flex;
        flex-direction: column;

        // remove inline margin from work headers
        :global(.is-panel-header) {
            --header-title-margin-inline: 0;
        }

        // remove top margin from first work header
        :global(.queue-list-container > .queue-list-work:first-child) {
            --header-title-padding-top: 0;
        }

        // work header title color
        :global(.header .title) {
            --header-title-color: var(--systemPrimary-onDark);
        }

        :global(.up-next-item::before) {
            border-top: none;
        }

        :global(.up-next-item-title .title) {
            font: var(--body);
            color: var(--systemPrimary-onDark);
        }

        :global(.up-next-item-title .subtitle),
        :global(.up-next-item-time) {
            color: var(--systemSecondary-onDark);
        }

        :global(.up-next-item-title .subtitle) {
            margin-top: 1px;
            font: var(--callout);
        }
    }

    .queue-section--autoplay {
        margin-top: 7px;
        background: linear-gradient(rgba(0, 0, 0, 0.1), transparent 150px);
    }

    .queue-header {
        height: $section-header-height;
        display: flex;
        align-items: center;
        justify-content: space-between;
        color: var(--systemPrimary-onDark);
    }

    .queue-header--sticky {
        position: sticky;
        top: 0;
        z-index: calc(var(--z-default) + 1);

        &.is-sticking {
            position: absolute;

            // Adjustments for margin -- RTL not needed
            left: 32px;
            right: 32px;
        }

        // When the Playing Next queue items has scrolled out of view, render the header
        // *after* the queue items so it can also be scrolled out of view.
        &.is-after-queue-list {
            order: 3;
        }
    }

    .queue-header__title {
        font: var(--title-3-emphasized);

        :global(svg) {
            height: 20px;
            width: 20px;
            fill: currentColor;
            vertical-align: -4px;
        }
    }

    .queue-header__caption {
        color: var(--systemSecondary-onDark);
        font: var(--callout);
    }

    .queue-list-container {
        :global(.artwork-wrapper) {
            border-radius: $global-border-radius-small;
        }

        :global(.artwork-component) {
            box-shadow: 0 3px 5px -4px rgba(0, 0, 0, 0.3);
        }
    }

    .queue-empty {
        // in order to center the message without making a scroll container, need to subtract the height of the now playing item
        height: calc(100% - #{$now-playing-item-height});
        display: flex;
        align-items: center;
        justify-content: center;
        font: var(--body);
        color: var(--systemSecondary-onDark);

        @media (--range-xsmall-down) {
            margin-top: 16dvh; // add 16% top margin to empty text for parity w/ iOS
        }
    }

    .queue-empty-message {
        padding: 0 32px;
        text-align: center;
    }

    .playing-next-controls-clear {
        font: var(--title-3);
    }

    @media (prefers-contrast: more) {
        .now-playing-queue {
            :global(.up-next-item-time) {
                color: var(--systemSecondary);
            }
        }

        .queue-header__caption {
            color: var(--systemSecondary);
        }

        .queue-section--autoplay {
            background: transparent; // Remove section scrim
        }
    }
</style>
